import React, { useContext } from 'react';
import cn from 'classnames';
import Loader from '@wix/da-ds/pkg/Loader';
import { useTranslation } from 'react-i18next';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import ProgressBar from '@wix/da-ds/pkg/ProgressBar';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { SubmitFormContext } from '../../../context';
import { useFilePreviewMainUploads } from '../../../hooks/useFilePreviewMainUploads';
import { useSubmitFormStatus } from '../../../hooks/useSubmitFormStatus';
import ImagePreview from '../ImagePreview';
import ReplacePreviewImageButton from '../ReplacePreviewImageButton';
import ReplaceSourceFileButton from '../ReplaceSourceFileButton';

import s from './FilePreview.scss';

export interface Props {
  className?: string;
}

const FilePreview: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const submitFormContext = useContext(SubmitFormContext);
  const { initResponse } = submitFormContext ?? {};
  const { isFormBusy, isMainUploading } = useSubmitFormStatus();
  const isMultiVideo = useFlag('multi_video');
  const canBeMultiMedia = !!initResponse?.deviation?.canBeMultiMedia;
  const showFileReplaceControls = !isMultiVideo || !canBeMultiMedia;

  const {
    needsPreview,
    isFilm,
    deviation,
    hasError,
    input,
    inputOrig,
    showOrigFilePicker,
    getDropzoneRootProps,
    isDragging,
    activeUploads,
    activeOrigUploads,
  } = useFilePreviewMainUploads();

  const isPublished = deviation?.isPublished;
  const showFakeFilmThumb = isFilm && !isPublished;

  const activeUpload = [...activeUploads, ...activeOrigUploads].pop();
  const uploadProgress = activeUpload?.progress;
  const allowUpload = !isFormBusy && !isMainUploading;

  return (
    <div className={cn(s['root'], className)}>
      {showFileReplaceControls && (
        <ReplaceSourceFileButton
          className={s['orig-btn-wrapper']}
          inputOrig={inputOrig}
          allowUpload={allowUpload}
          showOrigFilePicker={showOrigFilePicker}
        />
      )}
      {showFileReplaceControls && (
        <ReplacePreviewImageButton
          className={s['edit-btn-wrapper']}
          previewFileInput={allowUpload && !needsPreview ? input : null}
          allowUpload={allowUpload}
          needsPreview={!!needsPreview}
          getDropzoneRootProps={getDropzoneRootProps}
        />
      )}
      {needsPreview && allowUpload && (
        <>
          <div
            className={cn(
              s['need-preview-root'],
              isDragging && s['drag-active'],
              hasError && s['with-error']
            )}
            {...(allowUpload ? getDropzoneRootProps() : {})}
          >
            {input}
            <Loader
              className={s['llama']}
              size="big"
              state={showFakeFilmThumb ? 'popcorn' : 'static'}
            />
            <span className={s['title']}>{t('deviationSubmit.form.preview.file.title')}</span>
            <span className={cn(s['text'], hasError && s['has-error'])}>
              {t('deviationSubmit.form.preview.file.text')}
            </span>
            <GhostButton className={s['add-btn']} variant="main" size="large">
              {t('deviationSubmit.form.preview.file.btn')}
            </GhostButton>
          </div>
        </>
      )}
      {isMainUploading && (
        <div className={s['uploading-wrapper']}>
          <div className={s['uploading']}>
            <Loader size="big" state="eating" />
            {uploadProgress && (
              <ProgressBar progress={uploadProgress} className={s['progress-bar']} />
            )}
            <div className={s['progress-title']}>{t('uploadZone.progress.title')}</div>
          </div>
        </div>
      )}
      {allowUpload && !needsPreview && (
        <ImagePreview className={s['preview']} deviation={initResponse?.deviation} />
      )}
    </div>
  );
};
FilePreview.displayName = 'FilePreview';

export default FilePreview;
